import React from 'react';
import { Users, CheckCircle, Shield, Search, UserPlus, Edit3, Trash2 } from 'lucide-react';

interface User {
  id: string;
  email: string;
  full_name: string;
  role: 'admin' | 'auditor';
  is_active: boolean;
  created_at: string;
  $createdAt?: string; // Format Appwrite natif
}

interface UsersViewProps {
  users: User[];
  setShowCreateUser: (show: boolean) => void;
  onToggleUserStatus: () => void;
}

// 🔧 FONCTION UTILITAIRE CORRIGÉE pour les utilisateurs Appwrite
const formatDate = (user: User): string => {
  try {
    // 1. Essayer d'abord $createdAt (format natif Appwrite pour les utilisateurs)
    const dateString = user.$createdAt || user.created_at;
    
    console.log('🔍 Formatage date pour', user.email, ':', { 
      $createdAt: user.$createdAt, 
      created_at: user.created_at,
      selected: dateString 
    });

    if (!dateString) {
      return 'Date manquante';
    }

    let date: Date;

    // 2. Format ISO 8601 Appwrite (utilisateurs) : "2020-10-15T06:38:00.000+00:00"
    if (typeof dateString === 'string' && (
        dateString.includes('T') && 
        (dateString.includes('Z') || dateString.includes('+') || dateString.includes('-'))
    )) {
      date = new Date(dateString);
      console.log('📅 Format ISO détecté:', dateString, '→', date);
    }
    // 3. Timestamp Unix (documents Appwrite) : nombre en secondes
    else if (typeof dateString === 'number' || !isNaN(Number(dateString))) {
      const timestamp = Number(dateString);
      // Si c'est en secondes (timestamp < 10^10), convertir en millisecondes
      if (timestamp < 10000000000) {
        date = new Date(timestamp * 1000);
        console.log('⏰ Timestamp Unix secondes détecté:', timestamp, '→', date);
      } else {
        date = new Date(timestamp);
        console.log('⏰ Timestamp Unix millisecondes détecté:', timestamp, '→', date);
      }
    }
    // 4. Fallback : essayer de parser directement
    else {
      date = new Date(dateString);
      console.log('🎲 Parsing direct:', dateString, '→', date);
    }

    // 5. Vérifier si la date est valide
    if (isNaN(date.getTime())) {
      console.error('❌ Date invalide après parsing:', dateString, '→', date);
      return `Date invalide (${dateString})`;
    }

    // 6. Formater en français
    const formatted = date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    console.log('✅ Date formatée:', dateString, '→', formatted);
    return formatted;

  } catch (error) {
    console.error('💥 Erreur formatage date:', error, 'pour utilisateur:', user.email);
    return `Erreur (${user.created_at || user.$createdAt || 'N/A'})`;
  }
};

// 🔧 FONCTION pour formater la date relative
const formatRelativeDate = (user: User): string => {
  try {
    const dateString = user.$createdAt || user.created_at;
    if (!dateString) return '';
    
    let date: Date;
    
    if (typeof dateString === 'string' && dateString.includes('T')) {
      date = new Date(dateString);
    } else if (!isNaN(Number(dateString))) {
      const timestamp = Number(dateString);
      date = new Date(timestamp < 10000000000 ? timestamp * 1000 : timestamp);
    } else {
      date = new Date(dateString);
    }
    
    if (isNaN(date.getTime())) return '';
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return '';
    if (diffDays === 1) return 'Hier';
    if (diffDays < 7) return `Il y a ${diffDays} jours`;
    if (diffDays < 30) return `Il y a ${Math.floor(diffDays / 7)} semaines`;
    if (diffDays < 365) return `Il y a ${Math.floor(diffDays / 30)} mois`;
    return `Il y a ${Math.floor(diffDays / 365)} ans`;
  } catch (error) {
    return '';
  }
};

const UsersView: React.FC<UsersViewProps> = ({ 
  users, 
  setShowCreateUser, 
  onToggleUserStatus 
}) => {
  return (
    <div className="space-y-6 sm:space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Gestion des Utilisateurs</h2>
          <p className="text-gray-600 text-sm sm:text-base">Administrez votre équipe d'auditeurs ({users.length} utilisateurs)</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
            <input
              type="text"
              placeholder="Rechercher un utilisateur..."
              className="pl-9 sm:pl-10 pr-4 py-2 sm:py-3 bg-gray-50 border-0 rounded-xl sm:rounded-2xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all w-full sm:w-64 text-sm sm:text-base"
            />
          </div>
          <button
            onClick={() => setShowCreateUser(true)}
            className="flex items-center justify-center space-x-2 px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl sm:rounded-2xl font-bold hover:shadow-lg hover:scale-105 transform transition-all duration-200 text-sm sm:text-base"
          >
            <UserPlus className="w-4 h-4 sm:w-5 sm:h-5" />
            <span>Ajouter</span>
          </button>
        </div>
      </div>

      {/* Statistiques utilisateurs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-blue-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-600 font-semibold mb-2 text-sm sm:text-base">Total Utilisateurs</p>
              <p className="text-2xl sm:text-3xl font-black text-blue-900">{users.length}</p>
            </div>
            <div className="p-3 sm:p-4 bg-blue-500 rounded-xl sm:rounded-2xl">
              <Users className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-green-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-600 font-semibold mb-2 text-sm sm:text-base">Utilisateurs Actifs</p>
              <p className="text-2xl sm:text-3xl font-black text-green-900">{users.filter(u => u.is_active).length}</p>
            </div>
            <div className="p-3 sm:p-4 bg-green-500 rounded-xl sm:rounded-2xl">
              <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-purple-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-600 font-semibold mb-2 text-sm sm:text-base">Administrateurs</p>
              <p className="text-2xl sm:text-3xl font-black text-purple-900">{users.filter(u => u.role === 'admin').length}</p>
            </div>
            <div className="p-3 sm:p-4 bg-purple-500 rounded-xl sm:rounded-2xl">
              <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Liste des utilisateurs */}
      <div className="bg-white rounded-2xl sm:rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 sm:p-6 lg:p-8 border-b border-gray-100">
          <h3 className="text-lg sm:text-xl font-bold text-gray-900">Équipe ({users.length})</h3>
        </div>
        
        <div className="divide-y divide-gray-100">
          {users.map((user) => (
            <div key={user.id} className="p-4 sm:p-6 lg:p-8 hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-purple-50/50 transition-all duration-300 group">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-4 sm:space-y-0">
                <div className="flex items-center space-x-4 sm:space-x-6">
                  <div className="relative flex-shrink-0">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl sm:rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <span className="text-white font-bold text-sm sm:text-lg">
                        {user.full_name.split(' ').map(n => n[0]).join('').toUpperCase()}
                      </span>
                    </div>
                    <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 sm:w-5 sm:h-5 border-2 sm:border-3 border-white rounded-full ${
                      user.is_active ? 'bg-green-400' : 'bg-gray-400'
                    }`}></div>
                  </div>
                  
                  <div className="min-w-0 flex-1">
                    <h4 className="text-base sm:text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors truncate">
                      {user.full_name}
                    </h4>
                    <p className="text-gray-600 text-sm sm:text-base truncate">{user.email}</p>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-2">
                      <span className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-bold ${
                        user.role === 'admin' 
                          ? 'bg-purple-100 text-purple-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {user.role === 'admin' ? '👑 Admin' : '🔍 Auditeur'}
                      </span>
                      
                      {/* 🔧 DATE CORRIGÉE - DÉFINITIVE */}
                      <span className="text-xs text-gray-500 hidden sm:inline">
                        Créé le {formatDate(user)}
                      </span>
                      
                      {/* 🆕 DATE RELATIVE */}
                      {formatRelativeDate(user) && (
                        <span className="text-xs text-blue-500 hidden lg:inline">
                          ({formatRelativeDate(user)})
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                  <div className={`px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-bold text-center ${
                    user.is_active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {user.is_active ? '✅ Actif' : '❌ Inactif'}
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button className="p-2 text-blue-600 hover:text-blue-800 transition-colors">
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={onToggleUserStatus}
                      className="p-2 text-red-400 hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 🔧 DEBUG INFO (à laisser pour vérifier) */}
      {users.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm">
          <p className="font-bold text-yellow-800 mb-2">🔧 Debug dates (structure utilisateur):</p>
          {users.slice(0, 2).map(user => (
            <div key={user.id} className="text-yellow-700 mb-2">
              <div className="font-medium">{user.full_name} ({user.email}):</div>
              <div className="ml-4 text-xs font-mono bg-yellow-100 p-2 rounded">
                <div>• id: {user.id}</div>
                <div>• created_at: {user.created_at || 'undefined'}</div>
                <div>• $createdAt: {user.$createdAt || 'undefined'}</div>
                <div>• Formaté: {formatDate(user)}</div>
                <div>• Type created_at: {typeof user.created_at}</div>
                <div>• Type $createdAt: {typeof user.$createdAt}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UsersView;